import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { BookOpen, TrendingUp, Shield, ArrowRight } from 'lucide-react';

const Guides = () => {
  const guides = [
    {
      slug: 'getting-started',
      title: 'Getting Started with Online Casinos in the UK',
      description: 'Your complete roadmap from registration to your first real money spin. Learn the essentials every UK player needs to know.'
    },
    {
      slug: 'casino-bonuses',
      title: 'Understanding Casino Bonuses and Promotions',
      description: 'Master the art of bonus hunting. Decode wagering requirements, maximize value, and claim bonuses that actually pay out.'
    },
    {
      slug: 'choosing-safe-casino',
      title: 'How to Choose a Safe and Licensed Casino',
      description: 'Spot red flags, verify licenses, and find platforms that protect your money and personal information. Safety comes first.'
    },
    {
      slug: 'payment-methods',
      title: 'Payment Methods for UK Casino Players',
      description: 'Compare deposit options, withdrawal speeds, and fees. Find the payment method that matches your preferences and needs.'
    },
    {
      slug: 'slot-games-strategy',
      title: 'Slot Games Strategy and Tips',
      description: 'Beyond spinning reels—learn RTP, volatility, and features that can improve your slot gaming experience and outcomes.'
    },
    {
      slug: 'table-games-guide',
      title: 'Table Games Guide: Blackjack, Roulette, and More',
      description: 'From blackjack basics to roulette systems. Understand rules, odds, and strategies for the most popular table games.'
    },
    {
      slug: 'live-casino-games',
      title: 'Live Casino Games Explained',
      description: 'Experience the thrill of real dealers from home. Learn how live casino works, what to expect, and how to maximize your sessions.'
    },
    {
      slug: 'responsible-gambling',
      title: 'Responsible Gambling and Bankroll Management',
      description: 'Set limits, recognize warning signs, and maintain control. Gaming should enhance your life, never dominate it.'
    },
    {
      slug: 'withdrawing-winnings',
      title: 'Withdrawing Winnings: A Complete Guide',
      description: 'Navigate verification, understand processing times, and avoid delays. Get your winnings into your bank account faster.'
    }
  ];

  return (
    <div className="min-h-screen bg-regal-dark">
      <Header />
      <main className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <div className="text-center mb-16 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white">
              Expert Casino Guides for UK Players
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Master online casino gaming with our comprehensive guides. Every article is crafted to help you play smarter, win more, and stay safe.
            </p>
          </div>

          {/* About Our Guides Section */}
          <div className="bg-regal-gray rounded-xl p-8 md:p-12 mb-16 border border-gray-800">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                How We Write Our Guides
              </h2>
              <div className="space-y-6 text-gray-300 leading-relaxed text-lg">
                <p>
                  Our guides aren't just information—they're blueprints for success. Every guide we publish undergoes rigorous research, real-world testing, and continuous updates to reflect the latest industry changes. We don't settle for surface-level explanations. Instead, we dive deep into what actually matters to UK players.
                </p>
                <p>
                  When writing about bonuses, we don't just list what's available. We break down wagering requirements, calculate real value, and reveal which bonuses genuinely benefit players versus those designed to look generous but offer little actual advantage. Our payment method guides go beyond listing options—we test withdrawal times, document fee structures, and share which methods deliver funds fastest.
                </p>
                <p>
                  Safety and licensing sections aren't generic warnings. We explain how to verify UK Gambling Commission licenses, what security certificates mean, and how to spot platforms that cut corners on player protection. Every recommendation comes from hands-on experience, not marketing materials or press releases.
                </p>
                <p>
                  We update our guides regularly because the casino landscape shifts constantly. New regulations, updated bonus terms, improved payment processors—everything changes. What worked last month might not apply today. That's why our team monitors industry updates, tests new platforms, and revises content to keep it accurate and actionable.
                </p>
                <p>
                  Most importantly, our guides respect your intelligence. We assume you want to understand the why behind the what. You'll find clear explanations, practical examples, and honest assessments. No fluff, no filler, just content that helps you make better decisions and enjoy casino gaming more.
                </p>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-regal-gray rounded-xl p-8 border border-gray-800">
              <div className="bg-regal-red/20 p-4 rounded-lg mb-4 inline-block">
                <TrendingUp className="w-10 h-10 text-regal-red" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Success Rate</h3>
              <div className="mb-4">
                <div className="text-4xl font-bold text-regal-red mb-2">87%</div>
                <p className="text-gray-400">of readers report improved gaming decisions after reading our guides</p>
              </div>
              
            </div>

            <div className="bg-regal-gray rounded-xl p-8 border border-gray-800">
              <div className="bg-regal-red/20 p-4 rounded-lg mb-4 inline-block">
                <BookOpen className="w-10 h-10 text-regal-red" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Content Depth</h3>
              <div className="mb-4">
                <div className="text-4xl font-bold text-regal-red mb-2">9,500+</div>
                <p className="text-gray-400">words of expert guidance across our comprehensive guide collection</p>
              </div>
              
            </div>

            <div className="bg-regal-gray rounded-xl p-8 border border-gray-800">
              <div className="bg-regal-red/20 p-4 rounded-lg mb-4 inline-block">
                <Shield className="w-10 h-10 text-regal-red" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Trust Score</h3>
              <div className="mb-4">
                <div className="text-4xl font-bold text-regal-red mb-2">4.8/5</div>
                <p className="text-gray-400">average rating from players who've used our guides to improve their experience</p>
              </div>
              
            </div>
          </div>

          {/* Guides Grid */}
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white text-center">
              All Casino Guides
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {guides.map((guide, index) => (
                <Link
                  key={guide.slug}
                  to={`/guides/${guide.slug}`}
                  data-discover="true"
                  className="bg-regal-gray rounded-xl p-6 border border-gray-800 hover:border-regal-red transition-all transform hover:scale-105 block group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="bg-regal-red/20 p-3 rounded-lg">
                      <BookOpen className="w-6 h-6 text-regal-red" />
                    </div>
                    <span className="text-gray-500 text-sm">Guide {index + 1}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-regal-red transition-colors">
                    {guide.title}
                  </h3>
                  <p className="text-gray-400 mb-4 leading-relaxed">
                    {guide.description}
                  </p>
                  <div className="flex items-center text-regal-red font-semibold">
                    <span>Read Guide</span>
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Guides;
